import { Button } from "@/components/flexkit/Button";
import { Tooltip } from "@/components/Tooltip";
import { useMemo, forwardRef } from "react";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useListMCPIntegrations } from "@/queries/integration-queries";
import { useListHostAuthenticationTokens } from "@/queries/runner-configuration-queries";
import { IconMCP } from "@/assets/icons/geist/IconMCP";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";

type MCPIntegrationsButtonProps = {
    onClick: () => void;
    agentExecution: PlainAgentExecution;
    organizationId?: string;
} & PropsWithClassName;

export const MCPIntegrationsButton = forwardRef<HTMLButtonElement, MCPIntegrationsButtonProps>(function ToolsButton(
    { className, onClick, agentExecution, organizationId },
    ref,
) {
    // Check if any MCP integrations have issues
    const { data: integrations } = useListMCPIntegrations(organizationId);
    const { data: authTokensData } = useListHostAuthenticationTokens({ enabled: true });
    const hasIssues = useMemo(() => {
        if (!integrations || !authTokensData || !agentExecution.status?.mcpIntegrationStatuses) return false;

        const mcpStatuses = agentExecution.status.mcpIntegrationStatuses;
        const authTokenIntegrationIds = new Set(
            authTokensData.tokens.map((token) => token.integrationId).filter((s) => !!s),
        );

        // Only check integrations that have auth tokens
        return integrations
            .filter((integration) => authTokenIntegrationIds.has(integration.id))
            .some((integration) => {
                const status = mcpStatuses.find((s) => s.id === integration.id);
                return !!(status?.failureMessage || status?.warningMessage);
            });
    }, [integrations, authTokensData, agentExecution.status?.mcpIntegrationStatuses]);

    return (
        <Tooltip content="MCP Integrations">
            <div className="relative">
                <Button
                    ref={ref}
                    variant="ghost"
                    LeadingIcon={IconMCP}
                    className={cn(
                        "flex items-center justify-center rounded-lg p-2 text-content-secondary hover:bg-surface-button-clear-accent hover:text-content-accent disabled:cursor-not-allowed disabled:opacity-50",
                        className,
                    )}
                    aria-label="MCP Integrations"
                    onClick={onClick}
                    data-tracking-id="mcp-integrations-button"
                />
                {hasIssues && (
                    <div
                        className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-red-500"
                        aria-label="Integration issues detected"
                    />
                )}
            </div>
        </Tooltip>
    );
});
